import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import Receipts from "./pages/Receipts";
import BankStatements from "./pages/BankStatements";
import Matching from "./pages/Matching";
import VATReports from "./pages/VATReports";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";

const BACKEND_URL = window.RUNTIME_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "";

// Google OAuth Configuration
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const GOOGLE_SCOPES = [
  "openid",
  "email", 
  "profile",
  "https://www.googleapis.com/auth/drive.file"
].join(" ");

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Google Drive Context
const DriveContext = createContext(null);

export const useDrive = () => useContext(DriveContext);

// Load Google API Script
const loadGoogleScript = () => {
  return new Promise((resolve, reject) => {
    if (window.gapi) {
      resolve(window.gapi);
      return;
    }
    
    const script = document.createElement("script");
    script.src = "https://apis.google.com/js/api.js";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.gapi);
    script.onerror = reject;
    document.body.appendChild(script);
  });
};

// Load Google Identity Services
const loadGoogleIdentityServices = () => {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts) {
      resolve(window.google);
      return;
    }
    
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.body.appendChild(script);
  });
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gapiLoaded, setGapiLoaded] = useState(false);
  const [tokenClient, setTokenClient] = useState(null);

  // Initialize Google API
  useEffect(() => {
    const initGoogle = async () => {
      try {
        // Load both scripts
        await loadGoogleScript();
        await loadGoogleIdentityServices();
        
        // Initialize gapi client
        await new Promise((resolve) => {
          window.gapi.load("client", resolve);
        });
        
        // Initialize with Drive API discovery doc
        await window.gapi.client.init({
          discoveryDocs: ["https://www.googleapis.com/discovery/v1/apis/drive/v3/rest"]
        });
        
        // Load Drive API explicitly
        await window.gapi.client.load("drive", "v3");
        console.log("Google Drive API loaded successfully");
        
        // Create token client
        if (GOOGLE_CLIENT_ID) {
          const client = window.google.accounts.oauth2.initTokenClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: GOOGLE_SCOPES,
            callback: () => {} // Will be set during login
          });
          setTokenClient(client);
        }
        
        setGapiLoaded(true);
        setLoading(false);
      } catch (error) {
        console.error("Failed to initialize Google API:", error);
        setLoading(false);
      }
    };
    
    initGoogle();
  }, []);

  const login = useCallback(() => {
    if (!tokenClient) {
      console.error("Token client not initialized");
      return;
    }
    
    tokenClient.callback = async (response) => {
      if (response.error) {
        console.error("OAuth error:", response.error);
        return;
      }
      
      const token = response.access_token;
      setAccessToken(token);
      
      // Set token for gapi client
      window.gapi.client.setToken({ access_token: token });
      
      // Login to backend - this syncs user to MongoDB
      try {
        const backendResponse = await fetch(`${BACKEND_URL}/api/auth/login`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (backendResponse.ok) {
          const userData = await backendResponse.json();
          setUser({
            id: userData.id,
            google_id: userData.google_id,
            email: userData.email,
            name: userData.name,
            picture: userData.picture,
            global_role: userData.global_role,
            setup_complete: userData.setup_complete
          });
          console.log("User synced to backend:", userData);
        } else {
          console.error("Backend login failed, status:", backendResponse.status);
          // Fallback - default to pending so role selection shows
          const userInfoResponse = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
            headers: { Authorization: `Bearer ${token}` }
          });
          const userInfo = await userInfoResponse.json();
          
          setUser({
            id: userInfo.sub,
            email: userInfo.email,
            name: userInfo.name,
            picture: userInfo.picture,
            global_role: "pending"
          });
        }
      } catch (error) {
        console.error("Failed to sync user:", error);
        // Fallback - default to pending so role selection shows
        const userInfoResponse = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: { Authorization: `Bearer ${token}` }
        });
        const userInfo = await userInfoResponse.json();
        
        setUser({
          id: userInfo.sub,
          email: userInfo.email,
          name: userInfo.name,
          picture: userInfo.picture,
          global_role: "pending"
        });
      }
    };
    
    tokenClient.requestAccessToken({ prompt: "consent" });
  }, [tokenClient]);

  const logout = useCallback(() => {
    if (accessToken) {
      window.google.accounts.oauth2.revoke(accessToken, () => {
        console.log("Token revoked");
      });
    }
    window.gapi.client.setToken(null);
    setUser(null);
    setAccessToken(null);
  }, [accessToken]);

  const refreshToken = useCallback(() => {
    if (!tokenClient) return;
    
    tokenClient.callback = async (response) => {
      if (response.error) {
        console.error("Token refresh error:", response.error);
        logout();
        return;
      }
      
      setAccessToken(response.access_token);
      window.gapi.client.setToken({ access_token: response.access_token });
    };
    
    tokenClient.requestAccessToken({ prompt: "" });
  }, [tokenClient, logout]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser,
      accessToken, 
      loading, 
      gapiLoaded,
      login, 
      logout,
      refreshToken,
      isConfigured: !!GOOGLE_CLIENT_ID
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Drive Provider - Multi-Business Architecture with MongoDB Backend
const DriveProvider = ({ children }) => {
  const { accessToken, user, setUser, gapiLoaded } = useAuth();
  const [rootFolderId, setRootFolderId] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [initError, setInitError] = useState(null);
  
  // Multi-business state
  const [isAdmin, setIsAdmin] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [currentBusiness, setCurrentBusiness] = useState(null);
  const [businessFolderIds, setBusinessFolderIds] = useState({});
  
  // Role selection state for new users
  const [needsRoleSelection, setNeedsRoleSelection] = useState(false);
  const [selectingRole, setSelectingRole] = useState(false);

  // Initialize - fetch businesses from backend
  useEffect(() => {
    if (!accessToken || !gapiLoaded || !user) {
      setInitialized(false);
      setNeedsRoleSelection(false);
      return;
    }
    
    initializeAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, gapiLoaded, user]);

  // API helper
  const apiCall = async (endpoint, options = {}) => {
    const response = await fetch(`${BACKEND_URL}${endpoint}`, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || 'API error');
    }
    
    return response.json();
  };

  const findFolder = async (name, parentId = null) => {
    try {
      let query = `name='${name}' and mimeType='application/vnd.google-apps.folder' and trashed=false`;
      if (parentId) {
        query += ` and '${parentId}' in parents`;
      }
      
      const response = await window.gapi.client.drive.files.list({
        q: query,
        fields: "files(id, name, owners, capabilities)",
        spaces: "drive"
      });
      
      return response.result.files || [];
    } catch (error) {
      console.error(`Failed to find folder ${name}:`, error);
      throw error;
    }
  };

  const createFolder = async (name, parentId = null) => {
    try {
      const response = await window.gapi.client.drive.files.create({
        resource: {
          name: name,
          mimeType: "application/vnd.google-apps.folder",
          parents: parentId ? [parentId] : undefined
        },
        fields: "id"
      });
      return response.result.id;
    } catch (error) {
      console.error(`Failed to create folder ${name}:`, error);
      throw error;
    }
  };

  const findOrCreateFolder = async (name, parentId = null) => {
    const folders = await findFolder(name, parentId);
    if (folders.length > 0) {
      return folders[0].id;
    }
    return await createFolder(name, parentId);
  };

  const initializeAccess = async () => {
    setInitError(null);
    try {
      const userRole = user?.global_role || "pending";
      const setupComplete = user?.setup_complete === true;
      
      console.log(`User role: ${userRole}, setup_complete: ${setupComplete}`);

      // Super admin — skip ALL Drive initialization, go straight to dashboard
      if (userRole === "super_admin") {
        setInitialized(true);
        return;
      }

      // For all other users, initialize Google Drive
      if (window.gapi?.client) {
        window.gapi.client.setToken({ access_token: accessToken });
      } else {
        throw new Error("Google API client not loaded. Please refresh the page.");
      }

      // If setup not completed, show role selection
      if (!setupComplete) {
        setNeedsRoleSelection(true);
        setInitialized(true);
        return;
      }

      setIsAdmin(userRole === "admin");

      // Fetch businesses from backend
      try {
        const businessList = await apiCall('/api/businesses');
        console.log("Businesses from backend:", businessList);
        
        if (businessList.length > 0) {
          setBusinesses(businessList);
          
          // For admin with businesses, find existing Accruey folder (don't create)
          if (userRole === "admin") {
            try {
              const accrueyFolders = await findFolder("Accruey");
              if (accrueyFolders.length > 0) {
                setRootFolderId(accrueyFolders[0].id);
              }
            } catch (e) {
              console.log("Could not check Drive folder:", e.message);
            }
          }
          
          // Auto-select first business
          await selectBusiness(businessList[0]);
          setInitialized(true);
          return;
        }
      } catch (e) {
        console.log("Could not fetch businesses from backend:", e);
      }

      // No businesses found — just show dashboard
      // Admin sees "Create First Business", staff sees "No Access"
      // Drive folder will be created lazily when admin adds first business
      setInitialized(true);
      
    } catch (error) {
      console.error("Failed to initialize:", error);
      
      let errorMessage = "Failed to connect to Google Drive.";
      if (error.status === 403) {
        errorMessage = "Google Drive API access denied. Please enable the Drive API and grant permission.";
      } else if (error.status === 401) {
        errorMessage = "Authentication expired. Please log out and sign in again.";
      } else if (error.result?.error?.message) {
        errorMessage = error.result.error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setInitError(errorMessage);
    }
  };

  // Setup as Admin - set role via login endpoint query param
  const setupAsAdmin = async () => {
    setSelectingRole(true);
    const url = `${BACKEND_URL}/api/auth/login?role=admin`;
    console.log("setupAsAdmin calling:", url);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      
      if (!response.ok) {
        const err = await response.text();
        throw new Error(`Server error ${response.status} at ${url}: ${err}`);
      }
      
      const userData = await response.json();
      
      setUser({
        id: userData.id,
        google_id: userData.google_id,
        email: userData.email,
        name: userData.name,
        picture: userData.picture,
        global_role: userData.global_role,
        setup_complete: userData.setup_complete
      });
      setIsAdmin(true);
      setBusinesses([]);
      setNeedsRoleSelection(false);
    } catch (error) {
      console.error("Failed to setup as admin:", error);
      setInitError(`Backend URL: ${BACKEND_URL || "(empty)"} — ${error.message}`);
    } finally {
      setSelectingRole(false);
    }
  };

  // Continue as Staff - set role via login endpoint query param
  const continueAsStaff = async () => {
    setSelectingRole(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login?role=staff`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      
      if (!response.ok) {
        const err = await response.text();
        throw new Error(`Server error ${response.status}: ${err}`);
      }
      
      const userData = await response.json();
      
      setUser({
        id: userData.id,
        google_id: userData.google_id,
        email: userData.email,
        name: userData.name,
        picture: userData.picture,
        global_role: userData.global_role,
        setup_complete: userData.setup_complete
      });
      setIsAdmin(false);
      setNeedsRoleSelection(false);
      setBusinesses([]);
    } catch (error) {
      console.error("Failed to setup as staff:", error);
      setInitError(`Failed to set up: ${error.message}`);
    } finally {
      setSelectingRole(false);
    }
  };


  const selectBusiness = async (business) => {
    if (!business) return;
    
    setCurrentBusiness(business);
    
    // Get folder ID from business
    const driveFolderId = business.drive_folder_id || business.id;
    
    // Use different methods for admin vs staff
    if (isAdmin) {
      // Admin can create folders if needed
      try {
        const folderIds = {
          root: driveFolderId,
          receipts: await findOrCreateFolder("receipts", driveFolderId),
          bank_statements: await findOrCreateFolder("bank_statements", driveFolderId),
          exports: await findOrCreateFolder("exports", driveFolderId),
          vat_reports: await findOrCreateFolder("vat_reports", driveFolderId)
        };
        
        // Also ensure config folder exists at root level for admin
        if (rootFolderId) {
          folderIds.config = await findOrCreateFolder("config", rootFolderId);
        }
        
        setBusinessFolderIds(folderIds);
        console.log("Admin - Business folder structure loaded:", folderIds);
      } catch (error) {
        console.error("Failed to load business folders:", error);
      }
    } else {
      // Staff - only find existing folders
      try {
        const findExistingFolder = async (name, parentId) => {
          const response = await window.gapi.client.drive.files.list({
            q: `name='${name}' and '${parentId}' in parents and mimeType='application/vnd.google-apps.folder' and trashed=false`,
            fields: "files(id)",
            spaces: "drive"
          });
          return response.result.files?.[0]?.id || null;
        };
        
        const folderIds = {
          root: driveFolderId,
          receipts: await findExistingFolder("receipts", driveFolderId),
          bank_statements: await findExistingFolder("bank_statements", driveFolderId),
          exports: await findExistingFolder("exports", driveFolderId),
          vat_reports: await findExistingFolder("vat_reports", driveFolderId)
        };
        
        setBusinessFolderIds(folderIds);
        console.log("Staff - Business folders loaded:", folderIds);
      } catch (error) {
        console.error("Failed to load business folders for staff:", error);
      }
    }
  };

  const addBusiness = async (businessName) => {
    if (!isAdmin) {
      throw new Error("Only admin can add businesses");
    }
    
    try {
      // Step 1: Save to backend database FIRST (always works)
      const newBusiness = await apiCall('/api/businesses', {
        method: 'POST',
        body: JSON.stringify({
          name: businessName,
          drive_folder_id: null
        })
      });
      
      // Step 2: Try to create Drive folders (best-effort)
      try {
        // Ensure Accruey root folder exists
        let rootId = rootFolderId;
        if (!rootId) {
          const accrueyFolders = await findFolder("Accruey");
          if (accrueyFolders.length > 0) {
            rootId = accrueyFolders[0].id;
          } else {
            rootId = await createFolder("Accruey");
          }
          setRootFolderId(rootId);
        }
        
        // Create business folder
        const driveFolderId = await createFolder(businessName, rootId);
        
        // Create subfolders
        await Promise.all([
          createFolder("receipts", driveFolderId),
          createFolder("bank_statements", driveFolderId),
          createFolder("exports", driveFolderId),
          createFolder("vat_reports", driveFolderId)
        ]);
        
        // Update backend with Drive folder ID
        await apiCall(`/api/businesses/${newBusiness.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ drive_folder_id: driveFolderId })
        });
        
        newBusiness.drive_folder_id = driveFolderId;
        console.log("Business created with Drive folder:", driveFolderId);
      } catch (driveError) {
        console.warn("Drive folder creation failed — business saved to database only:", driveError.message);
      }
      
      // Update local state
      setBusinesses(prev => [...prev, newBusiness]);
      
      return newBusiness;
    } catch (error) {
      console.error("Failed to add business:", error);
      throw error;
    }
  };

  const deleteBusiness = async (businessId, driveFolderId) => {
    if (!isAdmin) {
      throw new Error("Only admin can delete businesses");
    }
    
    try {
      // Delete from Google Drive
      if (driveFolderId) {
        await window.gapi.client.drive.files.delete({ fileId: driveFolderId });
      }
      
      // Delete from backend
      await apiCall(`/api/businesses/${businessId}`, { method: 'DELETE' });
      
      // Update local state
      setBusinesses(prev => prev.filter(b => b.id !== businessId));
    } catch (error) {
      console.error("Failed to delete business:", error);
      throw error;
    }
  };

  const shareBusinessWithUser = async (businessId, email, driveFolderId, drivePermissionId = null) => {
    if (!isAdmin) {
      throw new Error("Only admin can share businesses");
    }
    
    try {
      // Share in Google Drive
      let permissionId = drivePermissionId;
      if (driveFolderId) {
        const response = await window.gapi.client.drive.permissions.create({
          fileId: driveFolderId,
          resource: {
            type: "user",
            role: "writer",
            emailAddress: email
          },
          sendNotificationEmail: true
        });
        permissionId = response.result.id;
      }
      
      // Save to backend
      const access = await apiCall(`/api/businesses/${businessId}/access`, {
        method: 'POST',
        body: JSON.stringify({
          business_id: businessId,
          user_email: email,
          drive_permission_id: permissionId,
          role: "staff"
        })
      });
      
      return access;
    } catch (error) {
      console.error("Failed to share business:", error);
      throw error;
    }
  };

  const getBusinessAccess = async (businessId) => {
    if (!isAdmin) return [];
    
    try {
      return await apiCall(`/api/businesses/${businessId}/access`);
    } catch (error) {
      console.error("Failed to get business access:", error);
      return [];
    }
  };

  const removeBusinessAccess = async (businessId, accessId, drivePermissionId, driveFolderId) => {
    if (!isAdmin) {
      throw new Error("Only admin can manage access");
    }
    
    try {
      // Remove from Google Drive
      if (drivePermissionId && driveFolderId) {
        try {
          await window.gapi.client.drive.permissions.delete({
            fileId: driveFolderId,
            permissionId: drivePermissionId
          });
        } catch (e) {
          console.log("Drive permission already removed or not found");
        }
      }
      
      // Remove from backend
      await apiCall(`/api/businesses/${businessId}/access/${accessId}`, { method: 'DELETE' });
      
      return true;
    } catch (error) {
      console.error("Failed to remove access:", error);
      throw error;
    }
  };

  const reloadBusinesses = async () => {
    try {
      const businessList = await apiCall('/api/businesses');
      setBusinesses(businessList);
    } catch (error) {
      console.error("Failed to reload businesses:", error);
    }
  };

  // Get month folder within receipts
  const getMonthFolder = async (parentFolderId, yearMonth) => {
    return await findOrCreateFolder(yearMonth, parentFolderId);
  };

  // Upload file to Drive
  const uploadFile = async (fileName, content, mimeType, folderId, existingFileId = null) => {
    try {
      const boundary = "-------314159265358979323846";
      const delimiter = "\r\n--" + boundary + "\r\n";
      const closeDelimiter = "\r\n--" + boundary + "--";
      
      const metadata = {
        name: fileName,
        mimeType: mimeType
      };
      
      if (!existingFileId) {
        metadata.parents = [folderId];
      }
      
      let base64Data;
      if (content instanceof Blob || content instanceof File) {
        base64Data = await blobToBase64(content);
      } else if (typeof content === "string") {
        base64Data = btoa(unescape(encodeURIComponent(content)));
      } else {
        base64Data = btoa(unescape(encodeURIComponent(JSON.stringify(content))));
      }
      
      const multipartRequestBody =
        delimiter +
        "Content-Type: application/json\r\n\r\n" +
        JSON.stringify(metadata) +
        delimiter +
        "Content-Type: " + mimeType + "\r\n" +
        "Content-Transfer-Encoding: base64\r\n\r\n" +
        base64Data +
        closeDelimiter;
      
      const url = existingFileId 
        ? `https://www.googleapis.com/upload/drive/v3/files/${existingFileId}?uploadType=multipart`
        : "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart";
      
      const method = existingFileId ? "PATCH" : "POST";
      
      const response = await fetch(url, {
        method: method,
        headers: {
          "Authorization": `Bearer ${accessToken}`,
          "Content-Type": `multipart/related; boundary=${boundary}`
        },
        body: multipartRequestBody
      });
      
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error("File upload error:", error);
      throw error;
    }
  };

  // Download file from Drive
  const downloadFile = async (fileId) => {
    try {
      const response = await window.gapi.client.drive.files.get({
        fileId: fileId,
        alt: "media"
      });
      return response.body;
    } catch (error) {
      console.error("File download error:", error);
      throw error;
    }
  };

  // Get file as JSON
  const getFileAsJson = async (fileId) => {
    const content = await downloadFile(fileId);
    return JSON.parse(content);
  };

  // List files in folder
  const listFiles = async (folderId, mimeType = null) => {
    try {
      let query = `'${folderId}' in parents and trashed=false`;
      if (mimeType) {
        query += ` and mimeType='${mimeType}'`;
      }
      
      const response = await window.gapi.client.drive.files.list({
        q: query,
        fields: "files(id, name, mimeType, createdTime, modifiedTime, size)",
        orderBy: "name desc"
      });
      
      return response.result.files || [];
    } catch (error) {
      console.error("List files error:", error);
      throw error;
    }
  };

  // Delete file
  const deleteFile = async (fileId) => {
    try {
      await window.gapi.client.drive.files.delete({
        fileId: fileId
      });
      return true;
    } catch (error) {
      console.error("Delete file error:", error);
      throw error;
    }
  };

  // Helper: Blob to Base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Create shareable link
  const createShareLink = async (fileId, expirationMinutes = 60) => {
    try {
      await window.gapi.client.drive.permissions.create({
        fileId: fileId,
        resource: {
          role: "reader",
          type: "anyone"
        }
      });
      
      const response = await window.gapi.client.drive.files.get({
        fileId: fileId,
        fields: "webViewLink, webContentLink"
      });
      
      return {
        viewLink: response.result.webViewLink,
        downloadLink: response.result.webContentLink
      };
    } catch (error) {
      console.error("Create share link error:", error);
      throw error;
    }
  };

  return (
    <DriveContext.Provider value={{
      initialized,
      initError,
      retryInit: initializeAccess,
      
      // Role selection
      needsRoleSelection,
      selectingRole,
      setupAsAdmin,
      continueAsStaff,
      
      // Multi-business
      isAdmin,
      businesses,
      currentBusiness,
      selectBusiness,
      addBusiness,
      deleteBusiness,
      shareBusinessWithUser,
      getBusinessAccess,
      removeBusinessAccess,
      reloadBusinesses,
      
      // Folder IDs for current business
      rootFolderId,
      folderIds: businessFolderIds,
      
      // File operations
      getMonthFolder,
      uploadFile,
      downloadFile,
      getFileAsJson,
      listFiles,
      deleteFile,
      createShareLink,
      blobToBase64
    }}>
      {children}
    </DriveContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, isConfigured } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isConfigured) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-md p-6">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Configuration Required</h2>
          <p className="text-slate-600 mb-4">
            Google OAuth is not configured. Please add <code className="bg-slate-100 px-1 rounded">REACT_APP_GOOGLE_CLIENT_ID</code> to your frontend/.env file.
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return <DriveProvider>{children}</DriveProvider>;
};

// Super Admin Route Component - no Drive context needed
const SuperAdminRoute = ({ children }) => {
  const { user, loading, isConfigured } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || user.global_role !== "super_admin") {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/receipts" element={<ProtectedRoute><Receipts /></ProtectedRoute>} />
      <Route path="/bank-statements" element={<ProtectedRoute><BankStatements /></ProtectedRoute>} />
      <Route path="/matching" element={<ProtectedRoute><Matching /></ProtectedRoute>} />
      <Route path="/vat-reports" element={<ProtectedRoute><VATReports /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/super-admin" element={<SuperAdminRoute><SuperAdminDashboard /></SuperAdminRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
